import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  Briefcase,
  Cake,
  Calendar,
  Coffee,
  Flame,
  Gift,
  Heart,
  ImageIcon,
  Leaf,
  Loader2,
  PartyPopper,
  Plus,
  Save,
  Shirt,
  Sparkles,
  Star,
  Store,
  Tag,
  Trash2,
  Truck,
  Utensils,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { readImages, type StorefrontImage } from '@/components/admin/storefront/storefrontConfig'

/**
 * Admin → Website → Features.
 *
 * Writes `storefront_extras`: the ordering site's optional pieces, each with
 * its own switch — Reorder, ordering for later, Best sellers, category picture
 * tiles, the promo carousel and the restaurant's own third header tab (with a
 * page of its own). Pictures come from the image library on the Design tab.
 */

type LinkKind = 'none' | 'category' | 'item' | 'page' | 'url'
interface LinkDraft {
  kind: LinkKind
  target: string
}
interface SlideDraft {
  id: string
  image_id: string
  title: string
  subtitle: string
  cta: string
  link: LinkDraft
}
interface TabDraft {
  enabled: boolean
  label: string
  icon: string
  kind: 'category' | 'page' | 'link'
  category_id: string
  url: string
  page: { title: string; subtitle: string; image_id: string; body: string; cta: string; cta_link: LinkDraft }
}
interface Draft {
  reorder: boolean
  best_sellers: boolean
  category_tiles: boolean
  scheduling: { enabled: boolean; days_ahead: number }
  carousel: { enabled: boolean; seconds: number; slides: SlideDraft[] }
  custom_tab: TabDraft
}

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  star: Star,
  gift: Gift,
  sparkles: Sparkles,
  utensils: Utensils,
  cake: Cake,
  coffee: Coffee,
  heart: Heart,
  briefcase: Briefcase,
  party: PartyPopper,
  tag: Tag,
  flame: Flame,
  leaf: Leaf,
  store: Store,
  calendar: Calendar,
  shirt: Shirt,
  truck: Truck,
}

const emptyLink = (): LinkDraft => ({ kind: 'none', target: '' })
const str = (v: unknown) => (typeof v === 'string' ? v : '')
const bool = (v: unknown, dflt: boolean) => (typeof v === 'boolean' ? v : dflt)

function readLink(v: unknown): LinkDraft {
  const o = (v ?? {}) as { kind?: string; target?: string }
  const kind = (['none', 'category', 'item', 'page', 'url'].includes(o.kind ?? '') ? o.kind : 'none') as LinkKind
  return { kind, target: str(o.target) }
}

function readDraft(v: unknown): Draft {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, any>
  const car = (o.carousel ?? {}) as Record<string, any>
  const tab = (o.custom_tab ?? {}) as Record<string, any>
  const page = (tab.page ?? {}) as Record<string, any>
  const sched = (o.scheduling ?? {}) as Record<string, any>
  return {
    reorder: bool(o.reorder, true),
    best_sellers: bool(o.best_sellers, true),
    category_tiles: bool(o.category_tiles, true),
    scheduling: {
      enabled: bool(sched.enabled, true),
      days_ahead: typeof sched.days_ahead === 'number' && sched.days_ahead >= 1 && sched.days_ahead <= 7 ? sched.days_ahead : 3,
    },
    carousel: {
      enabled: bool(car.enabled, false),
      seconds: typeof car.seconds === 'number' ? car.seconds : 5,
      slides: (Array.isArray(car.slides) ? car.slides : []).map((s: any, i: number) => ({
        id: str(s?.id) || `slide-${i + 1}`,
        image_id: str(s?.image_id),
        title: str(s?.title),
        subtitle: str(s?.subtitle),
        cta: str(s?.cta),
        link: readLink(s?.link),
      })),
    },
    custom_tab: {
      enabled: bool(tab.enabled, false),
      label: str(tab.label),
      icon: str(tab.icon) || 'star',
      kind: (['category', 'page', 'link'].includes(tab.kind) ? tab.kind : 'page') as TabDraft['kind'],
      category_id: str(tab.category_id),
      url: str(tab.url),
      page: {
        title: str(page.title),
        subtitle: str(page.subtitle),
        image_id: str(page.image_id),
        body: str(page.body),
        cta: str(page.cta),
        cta_link: readLink(page.cta_link),
      },
    },
  }
}

const isUrl = (s: string) => /^https?:\/\/\S+\.\S+/.test(s.trim())

export function AdminStorefrontFeatures() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const categoriesQuery = useQuery({ queryKey: ['categories', 'website-features'], queryFn: () => apiClient.getCategories(true) })
  const productsQuery = useQuery({ queryKey: ['products', 'website-features'], queryFn: () => apiClient.getAllProducts() })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined
  const images: StorefrontImage[] = useMemo(() => readImages(d?.storefront_images), [d])
  const categories = (categoriesQuery.data?.data ?? []) as Array<{ id: string; name: string }>
  const products = (productsQuery.data ?? []) as Array<{ id: string; name: string }>

  const [draft, setDraft] = useState<Draft>(() => readDraft(null))
  useEffect(() => {
    if (d) setDraft(readDraft(d.storefront_extras))
  }, [d])

  const set = (patch: Partial<Draft>) => setDraft((cur) => ({ ...cur, ...patch }))
  const setTab = (patch: Partial<TabDraft>) => setDraft((cur) => ({ ...cur, custom_tab: { ...cur.custom_tab, ...patch } }))
  const setPage = (patch: Partial<TabDraft['page']>) =>
    setDraft((cur) => ({ ...cur, custom_tab: { ...cur.custom_tab, page: { ...cur.custom_tab.page, ...patch } } }))
  const setSlides = (fn: (s: SlideDraft[]) => SlideDraft[]) =>
    setDraft((cur) => ({ ...cur, carousel: { ...cur.carousel, slides: fn(cur.carousel.slides) } }))

  // What would stop the site from showing a switched-on piece.
  const problems: string[] = []
  if (draft.carousel.enabled && !draft.carousel.slides.some((s) => s.image_id)) problems.push('The carousel needs at least one slide with a picture.')
  if (draft.carousel.slides.some((s) => s.link.kind === 'url' && !isUrl(s.link.target))) problems.push('A slide links to a web address that is not complete.')
  const t = draft.custom_tab
  if (t.enabled) {
    if (!t.label.trim()) problems.push('Give the third tab a name.')
    if (t.kind === 'category' && !t.category_id) problems.push('Choose the menu category the tab opens.')
    if (t.kind === 'link' && !isUrl(t.url)) problems.push('Enter the full web address the tab opens, starting with https://')
    if (t.kind === 'page' && !t.page.title.trim() && !t.page.body.trim()) problems.push('Give the tab’s page a title or some text.')
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.updateSetting('storefront_extras', {
        ...draft,
        carousel: { ...draft.carousel, slides: draft.carousel.slides.filter((s) => s.image_id) },
        custom_tab: { ...draft.custom_tab, label: draft.custom_tab.label.trim(), url: draft.custom_tab.url.trim() },
      }),
    onSuccess: () => {
      toast({ title: 'Website features saved', description: 'Your website shows the changes within a minute.' })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) => toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' }),
  })

  const TabIcon = ICONS[t.icon] ?? Star

  return (
    <div className="space-y-5">
      <Card className="divide-y divide-border/60 p-0">
        <div className="p-5 pb-3">
          <h3 className="text-sm font-semibold">Ordering</h3>
          <p className="text-xs text-muted-foreground">Shortcuts that help customers order faster and come back.</p>
        </div>
        <ToggleRow
          title="Reorder button"
          hint="Shows a returning customer their last order on the menu, with one button to put it all back in the cart."
          checked={draft.reorder}
          onChange={(v) => set({ reorder: v })}
        />
        <ToggleRow
          title="Order for later"
          hint="Customers can pick a day and time within your opening hours, including while you're closed. The time prints first on the kitchen ticket."
          checked={draft.scheduling.enabled}
          onChange={(v) => set({ scheduling: { ...draft.scheduling, enabled: v } })}
        >
          {draft.scheduling.enabled && (
            <div className="mt-3 flex items-center gap-3">
              <Label className="text-xs text-muted-foreground">How far ahead</Label>
              <Select
                value={String(draft.scheduling.days_ahead)}
                onValueChange={(v) => set({ scheduling: { ...draft.scheduling, days_ahead: Number(v) } })}
              >
                <SelectTrigger className="h-9 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n === 1 ? 'Today and tomorrow' : `Up to ${n} days`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </ToggleRow>
        <ToggleRow
          title="Best sellers"
          hint="A row of your most-ordered dishes from the last 30 days, with the price on a ribbon. Uses your featured dishes until there's enough history."
          checked={draft.best_sellers}
          onChange={(v) => set({ best_sellers: v })}
        />
        <ToggleRow
          title="Category picture tiles"
          hint="An “Explore menu” row with a picture for each category (its own photo, or its first dish's)."
          checked={draft.category_tiles}
          onChange={(v) => set({ category_tiles: v })}
        />
      </Card>

      <Card className="space-y-4 p-5">
        <div className="flex items-start gap-3 border-b border-border/60 pb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold">Promo carousel</h3>
            <p className="text-xs text-muted-foreground">
              Sliding banners at the top of your menu, in place of the hero. Each slide can open a category, a dish, your
              page or a link.
            </p>
          </div>
          <Switch
            checked={draft.carousel.enabled}
            onCheckedChange={(v) => set({ carousel: { ...draft.carousel, enabled: v } })}
            aria-label="Show the promo carousel"
          />
        </div>

        {images.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Slides use pictures from your image library. Add some on the{' '}
            <Link to="/admin/website" search={{ tab: 'design' }} className="font-medium text-primary underline-offset-2 hover:underline">
              Design tab
            </Link>{' '}
            first (wide pictures work best).
          </p>
        ) : (
          <>
            <ol className="space-y-3">
              {draft.carousel.slides.map((s, i) => (
                <li key={s.id} className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="flex-1 text-sm font-semibold">Slide {i + 1}</span>
                    <Button type="button" variant="ghost" size="icon" aria-label="Move up" disabled={i === 0} onClick={() => setSlides((l) => swap(l, i, i - 1))}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Move down"
                      disabled={i === draft.carousel.slides.length - 1}
                      onClick={() => setSlides((l) => swap(l, i, i + 1))}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Remove slide ${i + 1}`} onClick={() => setSlides((l) => l.filter((_, x) => x !== i))}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <ImagePick images={images} value={s.image_id} onChange={(id) => setSlides((l) => l.map((x, k) => (k === i ? { ...x, image_id: id } : x)))} />
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field label="Headline (optional)">
                      <Input value={s.title} maxLength={80} placeholder="Midnight deals are back" onChange={(e) => setSlides((l) => l.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} />
                    </Field>
                    <Field label="Button text (optional)">
                      <Input value={s.cta} maxLength={30} placeholder="Order now" onChange={(e) => setSlides((l) => l.map((x, k) => (k === i ? { ...x, cta: e.target.value } : x)))} />
                    </Field>
                    <Field label="Line under the headline (optional)" className="sm:col-span-2">
                      <Input value={s.subtitle} maxLength={160} onChange={(e) => setSlides((l) => l.map((x, k) => (k === i ? { ...x, subtitle: e.target.value } : x)))} />
                    </Field>
                  </div>
                  <LinkPick
                    className="mt-3"
                    label="Tapping the slide opens"
                    value={s.link}
                    categories={categories}
                    products={products}
                    allowPage={t.enabled && t.kind === 'page'}
                    onChange={(link) => setSlides((l) => l.map((x, k) => (k === i ? { ...x, link } : x)))}
                  />
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={draft.carousel.slides.length >= 8}
                onClick={() =>
                  setSlides((l) => [
                    ...l,
                    { id: `slide-${Date.now().toString(36)}`, image_id: images[0]?.id ?? '', title: '', subtitle: '', cta: '', link: emptyLink() },
                  ])
                }
              >
                <Plus className="h-4 w-4" /> Add slide
              </Button>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Next slide every</Label>
                <Select value={String(draft.carousel.seconds)} onValueChange={(v) => set({ carousel: { ...draft.carousel, seconds: Number(v) } })}>
                  <SelectTrigger className="h-9 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5, 6, 8, 10].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} seconds
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
      </Card>

      <Card className="space-y-4 p-5">
        <div className="flex items-start gap-3 border-b border-border/60 pb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold">Third header tab</h3>
            <p className="text-xs text-muted-foreground">
              A tab of your own next to Delivery and Pickup — catering, merch, gift cards, deals, events. You name it,
              pick its icon and decide what it opens.
            </p>
          </div>
          <Switch checked={t.enabled} onCheckedChange={(v) => setTab({ enabled: v })} aria-label="Show the third tab" />
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Tab name">
            <Input value={t.label} maxLength={20} placeholder="Catering" onChange={(e) => setTab({ label: e.target.value })} />
          </Field>
          <div>
            <p className="mb-1.5 text-xs text-muted-foreground">How it looks</p>
            <span className="inline-flex h-11 items-center gap-2 rounded-md border-2 border-transparent bg-muted px-5 text-[13px] font-bold uppercase tracking-wide">
              <TabIcon className="h-[18px] w-[18px] text-muted-foreground" /> {t.label.trim() || 'Your tab'}
            </span>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium">Icon</p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tab icon">
            {Object.entries(ICONS).map(([name, Icon]) => (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={t.icon === name}
                aria-label={name}
                onClick={() => setTab({ icon: name })}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md border-2 transition-colors',
                  t.icon === name ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'
                )}
              >
                <Icon className="h-5 w-5" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium">It opens</p>
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="What the tab opens">
            {(
              [
                ['page', 'Your own page', 'A page you write, with a picture and a button'],
                ['category', 'A menu category', 'Jumps to that part of your menu'],
                ['link', 'A web link', 'Another website, in a new tab'],
              ] as const
            ).map(([kind, title, hint]) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={t.kind === kind}
                onClick={() => setTab({ kind })}
                className={cn(
                  'rounded-md border-2 p-3 text-left transition-colors',
                  t.kind === kind ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                )}
              >
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </button>
            ))}
          </div>
        </div>

        {t.kind === 'category' && (
          <Field label="Category">
            <Select value={t.category_id || undefined} onValueChange={(v) => setTab({ category_id: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
        {t.kind === 'link' && (
          <Field label="Web address">
            <Input value={t.url} placeholder="https://" inputMode="url" onChange={(e) => setTab({ url: e.target.value })} />
          </Field>
        )}
        {t.kind === 'page' && (
          <div className="space-y-3 rounded-lg border p-4">
            <Field label="Page title">
              <Input value={t.page.title} maxLength={100} placeholder="Catering for your event" onChange={(e) => setPage({ title: e.target.value })} />
            </Field>
            <Field label="Line under the title">
              <Input value={t.page.subtitle} maxLength={240} placeholder="Platters for 10 to 500 guests, delivered on time." onChange={(e) => setPage({ subtitle: e.target.value })} />
            </Field>
            <div>
              <p className="mb-1.5 text-xs text-muted-foreground">Picture (optional)</p>
              {images.length === 0 ? (
                <p className="text-xs text-muted-foreground">Add pictures on the Design tab to use one here.</p>
              ) : (
                <ImagePick images={images} value={t.page.image_id} allowNone onChange={(id) => setPage({ image_id: id })} />
              )}
            </div>
            <Field label="Text">
              <Textarea
                value={t.page.body}
                maxLength={4000}
                rows={6}
                placeholder={'Tell customers what this is and how it works.\n\nLeave a blank line between paragraphs.'}
                onChange={(e) => setPage({ body: e.target.value })}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Button text (optional)">
                <Input value={t.page.cta} maxLength={30} placeholder="See the platters" onChange={(e) => setPage({ cta: e.target.value })} />
              </Field>
            </div>
            <LinkPick
              label="The button opens"
              value={t.page.cta_link}
              categories={categories}
              products={products}
              allowPage={false}
              onChange={(cta_link) => setPage({ cta_link })}
            />
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {problems.length > 0 && <span className="text-xs text-destructive">{problems[0]}</span>}
        <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || problems.length > 0} className="gap-1.5">
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save features
        </Button>
      </div>
    </div>
  )
}

function swap<T>(list: T[], a: number, b: number): T[] {
  if (b < 0 || b >= list.length) return list
  const next = [...list]
  ;[next[a], next[b]] = [next[b], next[a]]
  return next
}

function ToggleRow({
  title,
  hint,
  checked,
  onChange,
  children,
}: {
  title: string
  hint: string
  checked: boolean
  onChange: (v: boolean) => void
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
        {children}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={title} />
    </div>
  )
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  )
}

function ImagePick({
  images,
  value,
  onChange,
  allowNone,
}: {
  images: StorefrontImage[]
  value: string
  onChange: (id: string) => void
  allowNone?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Picture">
      {allowNone && (
        <button
          type="button"
          role="radio"
          aria-checked={!value}
          onClick={() => onChange('')}
          className={cn(
            'flex h-16 w-24 items-center justify-center rounded-md border-2 text-xs text-muted-foreground',
            !value ? 'border-primary' : 'border-border hover:border-primary/50'
          )}
        >
          <ImageIcon className="mr-1 h-4 w-4" /> None
        </button>
      )}
      {images.map((im) => (
        <button
          key={im.id}
          type="button"
          role="radio"
          aria-checked={value === im.id}
          aria-label={im.caption || 'Library picture'}
          onClick={() => onChange(im.id)}
          className={cn('h-16 w-24 overflow-hidden rounded-md border-2', value === im.id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50')}
        >
          <img src={im.src} alt="" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  )
}

function LinkPick({
  label,
  value,
  onChange,
  categories,
  products,
  allowPage,
  className,
}: {
  label: string
  value: LinkDraft
  onChange: (l: LinkDraft) => void
  categories: Array<{ id: string; name: string }>
  products: Array<{ id: string; name: string }>
  allowPage: boolean
  className?: string
}) {
  return (
    <div className={cn('grid gap-3 sm:grid-cols-2', className)}>
      <Field label={label}>
        <Select value={value.kind} onValueChange={(v) => onChange({ kind: v as LinkKind, target: '' })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nothing</SelectItem>
            <SelectItem value="category">A menu category</SelectItem>
            <SelectItem value="item">A dish</SelectItem>
            {allowPage && <SelectItem value="page">Your third-tab page</SelectItem>}
            <SelectItem value="url">A web link</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {value.kind === 'category' && (
        <Field label="Category">
          <Select value={value.target || undefined} onValueChange={(v) => onChange({ kind: 'category', target: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
      {value.kind === 'item' && (
        <Field label="Dish">
          <Select value={value.target || undefined} onValueChange={(v) => onChange({ kind: 'item', target: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a dish" />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
      {value.kind === 'url' && (
        <Field label="Web address">
          <Input value={value.target} placeholder="https://" inputMode="url" onChange={(e) => onChange({ kind: 'url', target: e.target.value })} />
        </Field>
      )}
    </div>
  )
}
